import { Lock } from 'lucide-react';

import { useRecordKeepingCc } from '@/hooks/useCompanySettings';
import type { EmailTemplateCategory } from '@/types';

interface LockedCcChipsProps {
  /** The template category whose record-keeping CC applies to this composer. */
  category: EmailTemplateCategory;
}

/**
 * The record-keeping CC addresses this email will be copied to no matter what.
 *
 * A DISPLAY of an enforcement that happens elsewhere: email.service merges these
 * in when the message is built, so the sender cannot drop one by editing the CC
 * box. Shown here only so nobody is surprised by a recipient they did not type,
 * and rendered as chips rather than as text in the CC field for the same reason -
 * an address in the field would look removable.
 *
 * Renders nothing when no address is configured for the category.
 */
const LockedCcChips = ({ category }: LockedCcChipsProps) => {
  const { data } = useRecordKeepingCc();
  const addresses = data?.[category] ?? [];

  if (addresses.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
        <Lock className="h-3 w-3" />
        Always copied:
      </span>
      {addresses.map((address) => (
        <span
          key={address}
          title="Added by the server on every send (Admin > Company Settings > Record-Keeping CC)"
          className="inline-flex items-center rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[11px] font-medium text-ink"
        >
          {address}
        </span>
      ))}
    </div>
  );
};

export default LockedCcChips;
